/**
 * A component for rendering the header bar Learn menu (under the label
 * "Subjects"). This component handles transforming the data from the server
 * into the correct format for displaying. It also manages its own state of
 * which pane of the dropdown is open.
 */

import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import throttle from "lodash.throttle";
import * as React from "react";
import {
    forwardRef,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelLarge} from "@khanacademy/wonder-blocks-typography";
import {useFeatureIsOnWithOverride} from "@khan/analytics-growthbook";
import {useRegionOverride} from "@khan/data-region-override";
import {mediaQueries, pageHeaderHeight} from "@khan/shared-styles";
import {staticUrl} from "@khan/urls";

import ScrollDisabler from "#components/modal-package/scroll-disabler.ts";

import DropdownButton from "../dropdown-button.tsx";
import HeaderDropdown from "../header-dropdown.tsx";
import type {DomainObject} from "../types.ts";

import CoursesMenuContainer from "./courses-menu-container.tsx";
import CoursesMenuContent from "./courses-menu-content.tsx";
import {CoursesMenuDropdown} from "./courses-menu-dropdown.tsx";

import caretDownIcon from "@phosphor-icons/core/fill/caret-down-fill.svg";
import caretUpIcon from "@phosphor-icons/core/fill/caret-up-fill.svg";

type Props = {
    // Extra aphrodite styles to add to the dropdown button.
    buttonStyles?: Array<any>;
    // A list of domains to display.
    domains: Array<DomainObject>;
    // Whether the domains query is being loaded
    loading?: boolean;
    // Whether the domains query returns an error
    error?: Error | null | undefined;
    // An event triggered when the dropdown button is clicked.
    onDropdownToggle?: (closingThroughLinkClick?: boolean) => void;
    // An event triggered when the dropdown is closed.
    onDropdownClose?: () => unknown;
    // Whether the dropdown should be visible.
    showDropdown?: boolean;
    // Modifies logo color depending on the header background color.
    backgroundColor?: "white" | "darkBlue";
    // True if the header scrolls with the content. This makes the dropdown
    // display as essentially a full-screen overlay.
    stickyHeader?: boolean;
    // True if the user is a teacher or logged-out user. Used to determine if
    // the browse by standards link should be rendered.
    shouldRenderStandardsGuideLink?: boolean;
};

/**
 * The top-level learn menu component, which includes both the header link and
 *  the dropdown component.
 */
const CoursesMenu = forwardRef<HTMLDivElement, Props>(
    (
        {
            buttonStyles = [],
            domains,
            loading = false,
            error,
            onDropdownToggle = () => {},
            onDropdownClose = () => {},
            showDropdown = false,
            backgroundColor = "white",
            stickyHeader,
            shouldRenderStandardsGuideLink,
        },
        ref,
    ): React.ReactElement => {
        const showV2 = useFeatureIsOnWithOverride("learn-menu-dropdown-v2");
        // The first child in the menu that we can navigate to. We use this to navigate
        // to the first link from the DropdownButton.
        const firstNavigableChildRef = useRef<HTMLElement | null | undefined>(
            null,
        );
        const dropdownButtonRef = useRef<HTMLElement | null | undefined>(null);

        // The distance from the top of the page to the top of the dropdown menu.
        // Only used when stickyHeader is true.
        const [menuTop, setMenuTop] = useState(0);

        const updateMenuTop = useCallback(() => {
            setMenuTop(
                // @ts-expect-error - TS2345 - Argument of type 'number | undefined' is not assignable to parameter of type 'SetStateAction<number>'.
                dropdownButtonRef.current?.getBoundingClientRect().bottom,
            );
        }, []);

        const shouldTrackMenuTop = stickyHeader && showDropdown;

        useEffect(() => {
            // If the header is sticky, we need to update the position of the
            // top of the menu when the DOM changes, since the header's height
            // may change (e.g. if a banner is dismissed).
            // We also need to update it when the window is resized. This
            // handles the case where:
            // - there is a banner at the top of the page
            // - the user scrolls down, hiding the banner
            // - the user opens the learn menu
            // - the user expands the window to be tall enough to show the whole
            //   page at once, so the banner is no longer hidden, and the header
            //   is pushed down
            const documentBody = document.body;
            if (!shouldTrackMenuTop || !documentBody) {
                return;
            }

            const mutationObserver = new MutationObserver(updateMenuTop);
            mutationObserver.observe(documentBody, {
                attributes: true,
                childList: true,
                subtree: true,
            });

            return () => mutationObserver.disconnect();
        }, [shouldTrackMenuTop, updateMenuTop]);

        // useCallback requires an inline function, so we need to use useMemo
        // to memoize the throttled function.
        const throttledUpdateMenuTop = useMemo(
            () => throttle(updateMenuTop, 100),
            [updateMenuTop],
        );
        useEffect(() => {
            // We also need to update the menu top position when the window is
            // resized. This handles the case where:
            // - there is a banner at the top of the page
            // - the user scrolls down, hiding the banner
            // - the user opens the learn menu
            // - the user expands the window to be tall enough to show the whole
            //   page at once, so the banner is no longer hidden, and the header
            //   is pushed down
            if (!shouldTrackMenuTop) {
                return;
            }

            window.addEventListener("resize", throttledUpdateMenuTop);

            return () =>
                window.removeEventListener("resize", throttledUpdateMenuTop);
        }, [shouldTrackMenuTop, throttledUpdateMenuTop]);

        const shouldUpdateMenuTopOnToggle = stickyHeader && !showDropdown;
        const handleDropdownToggle = useCallback(
            (closingThroughLinkClick = false) => {
                if (shouldUpdateMenuTopOnToggle) {
                    updateMenuTop();
                }
                onDropdownToggle(closingThroughLinkClick);
                // We don't mind triggering this every time the dropdown is toggled
                // because the user has to open it before they can close it.
            },
            [onDropdownToggle, updateMenuTop, shouldUpdateMenuTopOnToggle],
        );

        const handleLearnMenuClose = useCallback(() => {
            // We find and focus on the dropdown button. This is not necessary when
            // the dropdown menu is closed via the dropdown button, but it's important
            // when the learn menu is closed via the keyboard while the focus was inside
            // the learn menu contents.
            dropdownButtonRef.current?.focus();
            onDropdownClose();
        }, [onDropdownClose]);

        // Because this is a dropdown menu, we want users to be able to navigate
        // to the menu's contents from the dropdown button with the down arrow (but
        // only if the menu is already open).
        const handleArrowDown = useCallback(
            (event: React.KeyboardEvent) => {
                if (
                    showDropdown &&
                    firstNavigableChildRef.current &&
                    firstNavigableChildRef.current.firstChild &&
                    firstNavigableChildRef.current.firstChild instanceof
                        HTMLElement
                ) {
                    if (
                        firstNavigableChildRef.current instanceof
                            HTMLButtonElement ||
                        firstNavigableChildRef.current instanceof
                            HTMLAnchorElement
                    ) {
                        firstNavigableChildRef.current.focus();
                    } else {
                        firstNavigableChildRef.current.firstChild.focus();
                    }

                    event.preventDefault();
                } else if (!showDropdown) {
                    event.preventDefault();
                    onDropdownToggle();
                }
            },
            [onDropdownToggle, showDropdown],
        );

        const handleArrowUp = useCallback(
            (event: React.KeyboardEvent) => {
                if (showV2) {
                    event.preventDefault();
                    onDropdownToggle();
                }
                if (showDropdown) {
                    event.preventDefault();
                    onDropdownClose();
                }
            },
            [onDropdownClose, showDropdown, onDropdownToggle, showV2],
        );

        // Used to determine if the Browse By Standards link should be rendered.
        const regionResult = useRegionOverride();

        return (
            <div className={css(styles.wrapper)} ref={ref}>
                <DropdownButton
                    onClick={() => {
                        handleDropdownToggle();
                    }}
                    onEscape={onDropdownClose}
                    onArrowDown={handleArrowDown}
                    onArrowUp={handleArrowUp}
                    buttonStyles={buttonStyles}
                    testId="learn-menu-dropdown"
                    innerRef={(input) => (dropdownButtonRef.current = input)}
                    dropdownIsOpen={showDropdown}
                >
                    <LabelLarge
                        // NOTE(jeresig): This attribute is used by the
                        // content-library.js content-carousel.jsx to
                        // trigger the menu opening. Once we have a global
                        // Redux store to trigger that we should remove this.
                        data-learn-menu-trigger
                        testId="learn-menu-button"
                    >
                        {t`Explore`}
                        <span
                            className={css(
                                styles.triangle,
                                backgroundColor !== "white" &&
                                    styles.transparentTriangle,
                            )}
                        >
                            <PhosphorIcon
                                icon={
                                    showDropdown ? caretUpIcon : caretDownIcon
                                }
                                size="small"
                            />
                        </span>
                    </LabelLarge>
                </DropdownButton>
                {stickyHeader && showDropdown && <ScrollDisabler />}
                <HeaderDropdown
                    visible={showDropdown}
                    hasBorder={false}
                    dropdownStyles={
                        showV2
                            ? []
                            : [
                                  styles.dropdown,
                                  !loading && styles.dropdownBackground,
                                  stickyHeader && [
                                      styles.fixedScrollableDropdown,
                                      {
                                          top: menuTop,
                                      },
                                  ],
                              ]
                    }
                    hasDivWrapper={true}
                    // This id allows the spacer element in the BibliotronHomepage
                    // to get the height of this element on /.
                    // Because this element and the spacer element do not share a
                    // parent that is a React component, the only way to get the header
                    // dropdown's height to the spacer is by accessing it in the DOM
                    // via an id.
                    // TODO(diedra): Refactor BibliotronHomepage to add the LearnMenu
                    // to the flow of the page, rather than forcing the dropdown open,
                    // with its own close buttion so it doesn't need to interact with
                    // this component. Once that's done, this id can be removed.
                    id="header-dropdown"
                >
                    <CoursesMenuContainer loading={loading} error={error}>
                        {showV2 ? (
                            <CoursesMenuDropdown
                                domains={domains}
                                onClickLink={() => {
                                    handleDropdownToggle(true);
                                }}
                                onClose={handleLearnMenuClose}
                                region={regionResult}
                                shouldRenderStandardsGuideLink={
                                    !!shouldRenderStandardsGuideLink
                                }
                                ref={(nodes) =>
                                    nodes?.length &&
                                    (firstNavigableChildRef.current = nodes[0])
                                }
                            />
                        ) : (
                            <CoursesMenuContent
                                domains={domains}
                                onClickLink={() => {
                                    handleDropdownToggle(true);
                                }}
                                onEscape={handleLearnMenuClose}
                                captureFirstNavigableChildRef={(input) =>
                                    (firstNavigableChildRef.current = input)
                                }
                                region={regionResult}
                                shouldRenderStandardsGuideLink={
                                    shouldRenderStandardsGuideLink
                                }
                            />
                        )}
                    </CoursesMenuContainer>
                </HeaderDropdown>
            </div>
        );
    },
);

// Magic number specifying the lowerbound screen width in order for the
// learn menu background images to appear.
const BG_THRESHOLD = 1366;

const styles = StyleSheet.create({
    wrapper: {
        display: "inline-block",
        height: "100%",
        whiteSpace: "nowrap",

        [mediaQueries.smOrSmaller]: {
            display: "none",
        },
    },

    dropdown: {
        backgroundColor: color.offWhite,
        borderBottom: `1px solid ${color.offBlack16}`,
        borderTop: `1px solid ${color.offBlack16}`,
        left: 0,
        opacity: 0.99,
        width: "100%",
    },

    dropdownBackground: {
        [`@media screen and (min-width: ${BG_THRESHOLD}px)`]: {
            backgroundImage: `
                url("${staticUrl("/images/learn-menu/left-background.png")}"),
                url("${staticUrl("/images/learn-menu/right-background.png")}")
            `,
            backgroundPosition: `
                center left,
                top ${-pageHeaderHeight}px right 0px
            `,
            backgroundRepeat: "no-repeat",
        },
    },

    fixedScrollableDropdown: {
        position: "fixed",
        bottom: 0,
        overflowY: "auto",
    },

    triangle: {
        marginLeft: spacing.xxSmall_6,
        position: "relative",
    },

    transparentTriangle: {
        color: "rgba(255, 255, 255, 0.48)",
    },
});

export default CoursesMenu;
